<template>
  <div class="current-state-info">
    <v-checkbox
      v-model="defaultState"
      color="primary"
      class="current-state-info__initial-state"
      :class="{ invisible: !initialState }"
      :label="$t('common.fields.autoDefaultState')"
      hide-details
    />

    <div class="d-flex">
      <v-autocomplete
        v-model="filters.date"
        class="current-state-info__date"
        item-text="date"
        item-value="date"
        hide-details
        :label="$t('autoCurrentStateFilter.chooseFilter')"
        :items="stateDates"
      />

      <v-select
        v-if="isMultipleCurrentStates"
        v-model="currentStateIdx"
        class="current-state-info__time"
        item-text="text"
        item-value="value"
        :label="$t('common.fields.time')"
        :disabled="!filters.date"
        :items="currentStatesTimeList"
        hide-details
      />
    </div>

    <v-progress-circular
      v-if="loading"
      class="current-state-info__loader"
      color="warning"
      width="3"
      size="40"
      indeterminate
    />

    <div v-else-if="noCurrentState" class="current-state-info__no-data">
      <v-icon size="50" color="#777">mdi-car-off</v-icon>
      <b>{{ $t('autoCurrentState.noData') }}</b>
    </div>

    <div v-else class="current-state-info__content">
      <!-- <v-alert
        class="current-state-info__status d-inline-block"
        color="primary"
        icon="mdi-car-info"
        dense
        text
      >
        <b>{{ $t('common.fields.status') }}:</b>
        {{ $t('common.labels.fromInvestorState') }}
      </v-alert> -->

      <v-alert
        v-if="currentState.user"
        class="d-inline-block"
        color="primary"
        icon="mdi-calendar-range"
        dense
        text
      >
        {{ $t('autoCurrentState.mechanicAccept', { name: currentState.user }) }}
      </v-alert>

      <div class="current-state-info__photos">
        <v-carousel v-if="currentStateMedia" height="380" hide-delimiters>
          <v-carousel-item
            v-for="(item, key) in currentStateMedia"
            :key="key"
            class="current-state-info__carousel-item"
          >
            <div class="current-state-info__photos-label">
              {{ stateMediaParams[key] ? stateMediaParams[key].name : key }}

              <v-tooltip
                v-if="item.comment"
                color="warning"
                content-class="comment-tooltip"
                max-width="250"
                top
              >
                <template v-slot:activator="{ on, attrs }">
                  <div
                    v-on="on"
                    v-bind="attrs"
                    class="current-state-info__photo-comment"
                  >
                    <v-icon color="warning">
                      mdi-comment-text
                    </v-icon>
                  </div>
                </template>

                {{ item.comment }}
              </v-tooltip>
            </div>
            <video
              v-if="stateMediaParams[key] && stateMediaParams[key].is_video"
              class="current-state-info__video"
              controls
              :src="item.link"
            ></video>
            <!-- <v-img v-else class="current-state-info__photo" :src="item.link" /> -->
            <div
              class="current-state-info__photo"
              :style="{ backgroundImage: `url(${item.link})` }"
              @click="onPhotoClick(item.link)"
            />
          </v-carousel-item>
        </v-carousel>

        <div v-else class="current-state-info__photos-no-data">
          <v-icon size="50" color="#777">mdi-camera-off</v-icon>
          <b>{{ $t('mechanic.noMedia') }}</b>
        </div>
      </div>

      <div class="current-state-info__equipment">
        <b class="current-state-info__equipment-title">
          {{ $t('common.fields.autoCurrentEquipment') }}
        </b>
        <ul v-if="Object.keys(equipmentList).length">
          <li v-for="(item, key) in equipmentList" :key="key">
            <v-icon class="mr-1" size="15" color="warning"
              >mdi-check-circle-outline</v-icon
            >
            {{ getEquipmentItemText(key) }}
          </li>
        </ul>

        <div v-else class="current-state-info__equipment-no-data">
          {{ $t('common.fields.noData') }}
        </div>
      </div>

      <div class="current-state-info__check-list">
        <b class="current-state-info__check-list-title">
          {{ $t('auto.labels.currentTechnicalCondition') }}
        </b>
        <ul>
          <li v-for="(item, key) in autosState.checkList" :key="key">
            <v-icon
              v-if="
                currentState.checkList[key] &&
                  currentState.checkList[key].value === '1'
              "
              class="mr-1"
              size="15"
              color="warning"
            >
              mdi-check-circle-outline
            </v-icon>
            <v-icon v-else class="mr-1" size="15" color="red">
              mdi-close-circle-outline
            </v-icon>

            {{ getCheckListItemText(key) }}

            <v-tooltip
              v-if="getCheckListComment(key)"
              color="warning"
              content-class="comment-tooltip"
              max-width="300"
              top
            >
              <template v-slot:activator="{ on, attrs }">
                <div v-on="on" v-bind="attrs" class="ml-2">
                  <v-icon size="18" color="warning">
                    mdi-comment-text
                  </v-icon>
                </div>
              </template>

              {{ getCheckListComment(key) }}
            </v-tooltip>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    initialState: {
      type: Boolean,
      default: false,
    },
    initialDate: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    loading: false,
    currentStateIdx: 0,
    stateByDate: [],

    filters: {
      date: '',
    },
  }),
  watch: {
    'filters.date': {
      handler(val) {
        if (val) this.filters.defaultState = false;
      },
    },
    'filters.defaultState': {
      handler(val) {
        if (val) this.filters.date = '';
      },
    },
    filters: {
      deep: true,
      handler(params) {
        this.fetchAutoStateByDate(params);
      },
    },
  },
  computed: {
    defaultState: {
      get() {
        return this.stateDates[0].date === this.filters.date;
      },

      set(val) {
        if (val) {
          this.filters.date = this.stateDates[0].date;
        } else {
          this.filters.date = 0;
        }
      },
    },
    equipmentList() {
      return Object.keys(this.currentState.equipment).reduce((obj, key) => {
        if (this.currentState.equipment[key].value === '0') {
          return obj;
        }

        if (this.autosState.equipment[key]) {
          obj[key] = this.currentState.equipment[key]; // eslint-disable-line
        }

        return obj;
      }, {});
    },
    stateDates() {
      return this.$store.state.auto.stateDates;
    },
    autosState() {
      return this.$store.state.auto.autosState;
    },
    stateMediaParams() {
      const media = { ...this.autosState.media };
      const { additionalPhoto, additionalVideo } = this.currentState;

      if (additionalPhoto) {
        additionalPhoto.forEach((photo, idx) => {
          media[`additional-photo-${photo.key}`] = {
            name: `${this.$t('auto.labels.additionalPhoto')} ${idx + 1}`,
          };
        });
      }

      if (additionalVideo) {
        additionalVideo.forEach((video, idx) => {
          media[`additional-video-${video.key}`] = {
            is_video: true,
            name: `${this.$t('auto.labels.additionalVideo')} ${idx + 1}`,
          };
        });
      }

      return media;
    },
    isMultipleCurrentStates() {
      return this.stateByDate.length > 1;
    },
    currentStatesTimeList() {
      const formatNum = (num) => (num < 10 ? `0${num}` : num);

      return this.stateByDate.map(({ date }, idx) => {
        const stateDate = new Date(date);
        const hours = formatNum(stateDate.getHours());
        const minutes = formatNum(stateDate.getMinutes());

        return {
          text: `${hours}:${minutes}`,
          value: idx,
        };
      });
    },
    currentState() {
      const data = this.stateByDate[this.currentStateIdx];
      return data ? data.state : {};
    },
    currentStateMedia() {
      const { additionalPhoto, additionalVideo } = this.currentState;
      let {
        mediaComment,
        additionalPhotoComment,
        additionalVideoComment,
      } = this.currentState;

      const media = Object.keys(this.currentState.photo).reduce((obj, key) => {
        // eslint-disable-next-line
        obj[key] = {
          ...this.currentState.photo[key],
          comment: mediaComment[key] && mediaComment[key].value,
        };

        return obj;
      }, {});

      mediaComment = mediaComment || {};
      additionalPhotoComment = additionalPhotoComment.comment
        ? additionalPhotoComment.comment.value
        : null;
      additionalVideoComment = additionalVideoComment.comment
        ? additionalVideoComment.comment.value
        : null;

      if (additionalPhoto) {
        additionalPhoto.forEach((photo, idx) => {
          media[`additional-photo-${photo.key}`] = {
            ...photo,
            name: `${this.$t('auto.labels.additionalPhoto')} ${idx + 1}`,
            comment: additionalPhotoComment,
          };
        });
      }

      if (additionalVideo) {
        additionalVideo.forEach((video, idx) => {
          media[`additional-video-${video.key}`] = {
            ...video,
            name: `${this.$t('auto.labels.additionalVideo')} ${idx + 1}`,
            comment: additionalVideoComment,
          };
        });
      }

      return Object.keys(media).length ? media : null;
    },
    noCurrentState() {
      return Object.keys(this.currentState).length === 0;
    },
  },
  methods: {
    async fetchAutoStateByDate(params) {
      this.loading = true;
      const states = await this.$store.dispatch('auto/fetchAutoStateByDate', {
        autoId: this.$route.params.autoId,
        params,
      });

      const data = states.map(({ state, date, user }) => ({
        state: {
          additionalPhoto: state.additionalPhoto,
          additionalPhotoComment: state.additionalPhotoComment || {},
          additionalVideo: state.additionalVideo,
          additionalVideoComment: state.additionalVideoComment || {},
          checkList: state.checkList || {},
          checkListComment: state.checkListComment || {},
          equipment: state.equipment || {},
          mediaComment: state.mediaComment || {},
          photo: state.photo || {},
        },
        user,
        date,
      }));

      this.stateByDate = data;
      this.loading = false;
    },
    getEquipmentItemText(key) {
      const name = this.autosState.equipment[key];

      if (this.autosState.equipment_count.includes(key)) {
        return `${name} (${this.equipmentList[key].value} шт.)`;
      }

      return name;
    },
    getCheckListItemText(key) {
      const item = this.currentState.checkList[key];
      let option = '';

      if (!item || !item.value) {
        option = this.$t('common.absent');
      } else if (item.value === '0') {
        option = this.$t('common.faulty');
      } else if (item.value === '1') {
        option = this.$t('common.serviceable');
      }

      return `${this.autosState.checkList[key]} - ${option}`;
    },
    getCheckListComment(key) {
      const { checkListComment } = this.currentState;
      return checkListComment[key] ? checkListComment[key].value : '';
    },
    onPhotoClick(link) {
      this.$store.commit('global/setImageViewerLink', link);
      this.$store.commit('global/setImageViewer', true);
    },
  },
  created() {
    if (this.initialState) this.defaultState = true;
  },
};
</script>

<style lang="scss" scoped>
.current-state-info {
  &__date,
  &__time {
    max-width: 200px;
  }

  &__time {
    margin-left: 30px;
  }

  &__loader {
    display: block;
    margin: 50px auto;
  }

  &__content {
    margin-top: 20px;
  }

  &__no-data,
  &__photos-no-data {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 380px;
    border-radius: 5px;
    background-color: #eee;

    b {
      color: #777;
      margin-top: 5px;
      font-size: 18px;
    }
  }

  &__no-data {
    margin-top: 20px;
  }

  &__carousel-item {
    position: relative;
  }

  &__photos-label {
    position: absolute;
    top: 0;
    left: 0%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 10px 10px;
    font-size: 15px;
    font-weight: 500;
    color: #464646;
    background-color: rgba(238, 238, 238, 0.7);
    z-index: 1;
  }

  &__photo-comment {
    padding: 5px;
    background-color: #fff;
    border-radius: 5px;
  }

  &__video {
    display: block;
    width: 100%;
    height: 100%;
  }

  &__photo {
    width: 100%;
    height: 100%;
    background-color: #d1d1d1;
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;
    cursor: pointer;
  }

  &__equipment,
  &__check-list {
    margin-top: 30px;

    &-title {
      display: block;
      font-size: 18px;
      color: #464646;
      margin-bottom: 10px;
    }

    ul {
      padding: 0;
      margin: 0;
      max-height: 300px;
      overflow-y: auto;

      &::-webkit-scrollbar {
        width: 10px;
        background-color: transparent;
      }
      &::-webkit-scrollbar-thumb {
        background-color: rgba(0, 0, 0, 0.15);
        background-clip: padding-box;
        border-radius: 20px;
        border: solid 2px rgba(0, 0, 0, 0);
      }
      &::-webkit-scrollbar-button {
        width: 0;
        height: 0;
        display: none;
      }
      &::-webkit-scrollbar-corner {
        background-color: transparent;
      }

      li {
        display: flex;
        align-items: center;
        list-style: none;
        font-size: 15px;
      }
    }
  }

  &__equipment-no-data {
    font-size: 15px;
    color: #777;
  }

  .comment-tooltip {
    word-break: break-word;
  }

  .invisible {
    opacity: 0;
    pointer-events: none;
  }
}
</style>
